import React, { useState } from 'react';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, Briefcase, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { warehouseAPI } from '../lib/api';
import { StockRowChoice, getStockRowOptions, apiErrorText } from './StockRowChoice';
import { RequestMatchChoice } from './RequestMatchChoice';

export const AssignToJobModal = ({ 
  open, 
  onOpenChange, 
  item,
  warehouseId,
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    job_name: '', job_number: '', quantity: 1, notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stockOptions, setStockOptions] = useState(null);
  const [stockRowId, setStockRowId] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchChoice, setMatchChoice] = useState(null);

  React.useEffect(() => {
    if (open) {
      setFormData({ job_name: '', job_number: '', quantity: 1, notes: '' });
      setError(null);
      setStockOptions(null);
      setStockRowId(null);
      setMatches([]);
      setMatchChoice(null);
    }
  }, [open]);

  const applyMatches = (list) => {
    setMatches(list);
    setMatchChoice(list.length === 1 ? list[0].part_request_item_id : null);
  };

  const jobNumber = formData.job_number.trim();
  React.useEffect(() => {
    if (!open || !item || !jobNumber) { setMatches([]); setMatchChoice(null); return undefined; }
    const t = setTimeout(() => {
      warehouseAPI.requestMatches({ warehouse_id: warehouseId, item_id: item.item_id, job_number: jobNumber })
        .then((r) => applyMatches(r.data.matches || []))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [open, item, warehouseId, jobNumber]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const availableQty = Math.max(0, item?.available_qty ?? item?.qty_on_hand ?? 0);
  const selectedMatch = matches.find((m) => m.part_request_item_id === matchChoice) || null;
  // A linked request's own reservation is converted, not double-counted.
  const ownReserved = selectedMatch?.holds_reservation ? Math.min(selectedMatch.reserved_held ?? selectedMatch.outstanding, item?.reserved_qty || 0) : 0;
  const maxQty = selectedMatch ? Math.min(selectedMatch.outstanding, availableQty + ownReserved) : availableQty;
  const isOverAllocation = formData.quantity > maxQty;

  const validateForm = () => {
    if (!formData.job_name.trim()) { setError('Job name is required'); return false; }
    if (formData.quantity < 1) { setError('Quantity must be at least 1'); return false; }
    if (matches.length && !matchChoice) { setError('Choose the part request this assignment is for, or "Not for a part request"'); return false; }
    if (formData.quantity > maxQty) { setError(`Cannot assign more than available (${maxQty})`); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setError(null);
    try {
      const response = await warehouseAPI.assignToJob({
        item_id: item.item_id, warehouse_id: warehouseId,
        job_name: formData.job_name.trim(), job_number: formData.job_number.trim() || null,
        quantity: parseInt(formData.quantity), notes: formData.notes.trim() || null,
        warehouse_inventory_id: stockRowId || item.id || null,
        part_request_item_id: selectedMatch ? selectedMatch.part_request_item_id : null,
        no_request_match: matchChoice === 'none',
      });
      const d = response.data;
      toast.success(
        `Assigned ${formData.quantity}x ${item.item_name} to job "${formData.job_name}"`,
        { description: d.part_request_id
          ? `Linked to part request #${d.part_request_id.slice(0, 8)} (${d.part_request_status}); ${d.reservation_released} reserved released`
          : `New inventory: ${d.new_inventory_quantity}` }
      );
      onSuccess?.(response.data);
      onOpenChange(false);
    } catch (err) {
      // Cloudflare 524 / network-timeout path: the assign call may have
      // already committed on the backend (commit happens before background
      // notification). Surface a specific message so the operator refreshes
      // instead of retrying and double-assigning.
      const isTimeout =
        err?.code === 'ECONNABORTED' ||
        /timeout|timed out|Network Error/i.test(err?.message || '') ||
        err?.response?.status === 524 ||
        err?.response?.status === 504 ||
        err?.response?.status === 408;
      const options = getStockRowOptions(err);
      if (options) { setStockOptions(options); setStockRowId(null); }
      const detail = err?.response?.data?.detail;
      if (detail?.code === 'part_request_match_required') applyMatches(detail.matches || []);
      const errorMsg = isTimeout
        ? 'The assignment may still be processing. Please refresh before trying again to avoid a duplicate assignment.'
        : apiErrorText(err, 'Failed to assign to job');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  const isDirty = formData.job_name !== '' || formData.notes !== '';

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        <span className="flex items-center gap-2">
          <Briefcase className="w-5 h-5" />
          Assign to Job
        </span>
      }
      description="Allocate warehouse inventory to a job. If the job has an open part request for this part, link it so its reservation is released."
      size="md"
      isDirty={isDirty}
      data-testid="assign-to-job-drawer"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="assign-job-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || isOverAllocation || maxQty === 0 || (matches.length > 0 && !matchChoice)} data-testid="assign-job-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Assign to Job
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Item Info */}
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-medium">{item.item_name}</p>
          <p className="text-sm text-muted-foreground">
            Available: <span className="font-medium" data-testid="assign-job-available-qty">{availableQty}</span>
            {item.sku && <span className="ml-2">SKU: {item.sku}</span>}
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-testid="assign-job-qty-breakdown">
            On hand {item.qty_on_hand ?? 0} · Reserved {item.reserved_qty ?? 0} · Allocated {item.allocated_qty ?? 0}
            {(item.quarantined_qty ?? 0) > 0 && ` · Quarantined ${item.quarantined_qty}`}
          </p>
        </div>

        {/* Job Name */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Job Name <span className="text-red-500">*</span></Label>
          <Input value={formData.job_name} onChange={(e) => handleChange('job_name', e.target.value)}
            placeholder="e.g., Smith Residence HVAC Install" className="h-12"
            data-testid="assign-job-name-input" autoFocus />
        </div>

        {/* Job Number */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Job Number</Label>
          <Input value={formData.job_number} onChange={(e) => handleChange('job_number', e.target.value)}
            placeholder="e.g., JOB-2024-001" className="h-12 font-mono"
            data-testid="assign-job-number-input" />
        </div>

        {/* Quantity */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Quantity to Assign <span className="text-red-500">*</span></Label>
          <div className="flex items-center gap-2">
            <Input type="number" min="1" max={maxQty} value={formData.quantity}
              onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 0)}
              className={`w-24 h-12 font-mono ${isOverAllocation ? 'border-red-500' : ''}`}
              data-testid="assign-job-quantity-input" />
            <span className="text-sm text-muted-foreground" data-testid="assign-job-max-qty">
              of {maxQty} {selectedMatch ? 'for this request' : 'available'}
            </span>
            {maxQty > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => handleChange('quantity', maxQty)} className="text-xs">
                All
              </Button>
            )}
          </div>
          {isOverAllocation && (
            <p className="text-xs text-red-500 flex items-center gap-1" data-testid="assign-job-qty-error">
              <AlertCircle className="w-3 h-3" />{maxQty === 0 ? 'No stock available to assign' : 'Cannot exceed available quantity'}
            </p>
          )}
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Notes</Label>
          <Textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Optional notes about this allocation..." rows={2} className="resize-none"
            data-testid="assign-job-notes-input" />
        </div>

        <RequestMatchChoice matches={matches} value={matchChoice} onChange={(v) => { setMatchChoice(v); setError(null); }} />

        <StockRowChoice options={stockOptions} value={stockRowId} onChange={setStockRowId} />

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
          </div>
        )}
      </div>
    </AppDrawer>
  );
};

export default AssignToJobModal;
